import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminErrors, ErrorEvent, ErrorPlace } from '../api/adminErrorsApi';
import { fmtCalendarDay, fmtDateTime, fmtInt } from '../model/format';
import { Delta, Panel, Tile } from './AdminAnalytics';
import { SERIES_COLORS, TimeChart } from './TimeChart';

const ERROR_COLOR = SERIES_COLORS[1];
const RECENT_FOLDED = 8;
/** Все записи, которые считает журнал ошибок: ожидаемые отказы и повторы пишутся WARNING-ом. */
const ERRORS_QUERY = 'levelname:ERROR or levelname:CRITICAL';

/** Значение KQL в кавычках. */
const kql = (value: string) => `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
/** Строка rison в кавычках: «!» и «'» экранируются восклицательным знаком. */
const rison = (value: string) => `'${value.replace(/!/g, '!!').replace(/'/g, "!'")}'`;

/** Discover с готовым запросом за интервал: `now-7d` или ISO-время. Представление данных —
 *  по умолчанию (lomora-logs-*), его один раз создают в Kibana — см. operations.md. */
function discoverUrl(base: string, query: string, from: string, to = 'now'): string {
  const g = `(time:(from:${rison(from)},to:${rison(to)}))`;
  const a = `(query:(language:kuery,query:${rison(query)}))`;
  return `${base.replace(/\/+$/, '')}/app/discover#/?_g=${encodeURIComponent(g)}&_a=${encodeURIComponent(a)}`;
}

function around(iso: string, minutes: number): { from: string; to: string } {
  const at = new Date(iso).getTime();
  return { from: new Date(at - minutes * 60_000).toISOString(), to: new Date(at + minutes * 60_000).toISOString() };
}

function KibanaLink({ href, children }: { href: string; children: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="shrink-0 text-accent hover:underline">
      {children}
    </a>
  );
}

function PlaceList({ rows, kibana, days, lang }: { rows: ErrorPlace[]; kibana: string | null; days: number; lang: string }) {
  const { t } = useTranslation();
  const max = Math.max(1, ...rows.map((row) => row.count));
  return (
    <ul className="space-y-3">
      {rows.map((row) => (
        <li key={`${row.place}\u001f${row.errorType}`} className="min-w-0">
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 break-words font-mono text-[12px] text-fg">{row.place}</span>
            <span className="shrink-0 text-[14px] font-semibold tabular-nums text-fg">{fmtInt(row.count)}</span>
          </div>
          <div className="mt-1 h-1 rounded-full bg-[#2a2a30]">
            <div className="h-full rounded-full" style={{ width: `${(row.count / max) * 100}%`, background: ERROR_COLOR }} />
          </div>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[12px] text-muted">
            <span className="break-words text-[#f47a7e]">{row.errorType}</span>
            {row.origin && <span className="break-all font-mono">{row.origin}</span>}
            {row.lastAt && <span>{t('admin.errors.last', { time: fmtDateTime(row.lastAt, lang) })}</span>}
            {kibana && (
              <KibanaLink
                href={discoverUrl(
                  kibana,
                  `(${ERRORS_QUERY}) and error_place:${kql(row.place)} and error_type:${kql(row.errorType)}`,
                  `now-${days}d`,
                )}
              >
                {t('admin.errors.inKibana')}
              </KibanaLink>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Ссылка на всё, что записал этот запрос или задача (по request_id / job_id), а без него —
 *  на ошибки того же места в пяти минутах вокруг. */
function eventUrl(kibana: string, row: ErrorEvent): string {
  if (row.requestId) {
    const range = around(row.at, 30);
    return discoverUrl(kibana, `request_id:${kql(row.requestId)}`, range.from, range.to);
  }
  const range = around(row.at, 5);
  return discoverUrl(kibana, `(${ERRORS_QUERY}) and error_place:${kql(row.place)}`, range.from, range.to);
}

function RecentList({ rows, kibana, lang }: { rows: ErrorEvent[]; kibana: string | null; lang: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const shown = open ? rows : rows.slice(0, RECENT_FOLDED);
  return (
    <>
      <ul className="divide-y divide-line">
        {shown.map((row, index) => (
          <li
            key={`${row.at}-${index}`}
            className="flex flex-col gap-0.5 py-2.5 first:pt-0 last:pb-0 sm:flex-row sm:items-baseline sm:gap-4"
          >
            <span className="shrink-0 text-[12px] tabular-nums text-muted sm:w-[120px]">{fmtDateTime(row.at, lang)}</span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="break-words font-mono text-[12px] text-fg">{row.place}</span>
                <span className="break-words text-[12px] text-[#f47a7e]">{row.errorType}</span>
                {row.status && <span className="text-[12px] tabular-nums text-muted">{row.status}</span>}
              </div>
              <div className="mt-0.5 flex flex-wrap gap-x-2 text-[12px] text-muted">
                {row.message && row.message !== row.errorType && <span className="break-words">{row.message}</span>}
                {row.origin && <span className="break-all font-mono">{row.origin}</span>}
                {row.component && <span>{row.component}</span>}
              </div>
            </div>
            {kibana && (
              <span className="text-[12px]">
                <KibanaLink href={eventUrl(kibana, row)}>{t('admin.errors.inKibana')}</KibanaLink>
              </span>
            )}
          </li>
        ))}
      </ul>
      {rows.length > RECENT_FOLDED && (
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="mt-3 text-[14px] font-medium text-muted transition-colors hover:text-fg"
        >
          {open ? t('admin.errors.showLess') : t('admin.errors.showAll', { n: rows.length })}
        </button>
      )}
    </>
  );
}

/**
 * Ошибки бэкенда (2026-10-13): журнал ERROR+ из API и воркеров. Счётчики за сегодня, 7 и
 * 30 дней, ряд по дням и «где падает» — за выбранный период; подробности и traceback —
 * по ссылкам в Kibana с готовым фильтром.
 */
export function AdminErrorsView({ data, id }: { data: AdminErrors; id?: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'ru';
  const { days, series, kibanaUrl } = data;
  const tracked = data.since !== null;
  const value = (n: number) => (tracked ? fmtInt(n) : '—');
  const yesterday = series.length > 1 ? series[series.length - 2].count : null;
  const since = data.since && series.length && data.since > series[0].day ? fmtCalendarDay(data.since, lang, 'long') : null;

  const tiles = [
    {
      label: t('admin.errors.today'),
      value: value(data.today),
      sub: tracked ? t('admin.errors.yesterday', { n: fmtInt(yesterday ?? 0) }) : t('admin.errors.notTracked'),
      // На телефоне «сегодня» — во всю ширину, неделя и месяц — рядом под ним.
      className: 'col-span-2 sm:col-span-1',
    },
    {
      label: t('admin.errors.week'),
      value: value(data.week),
      delta: tracked ? <Delta now={data.week} prev={data.weekPrev} goodUp={false} /> : undefined,
      sub: t('admin.errors.prev', { days: 7, n: fmtInt(data.weekPrev) }),
    },
    {
      label: t('admin.errors.month'),
      value: value(data.month),
      delta: tracked ? <Delta now={data.month} prev={data.monthPrev} goodUp={false} /> : undefined,
      sub: t('admin.errors.prev', { days: 30, n: fmtInt(data.monthPrev) }),
    },
  ];

  return (
    // scroll-mt: на телефоне сверху фиксированные кнопки меню и баланса.
    <section id={id} className="scroll-mt-16 space-y-3 nav:scroll-mt-6" aria-labelledby="admin-errors-title">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div className="min-w-0">
          <h2 id="admin-errors-title" className="font-serif text-[22px] font-medium tracking-[-0.01em]">
            {t('admin.errors.title')}
          </h2>
          <div className="mt-1 max-w-[720px] text-[14px] leading-relaxed text-muted">{t('admin.errors.sub')}</div>
        </div>
        {kibanaUrl && (
          <a
            href={discoverUrl(kibanaUrl, ERRORS_QUERY, `now-${days}d`)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-9 shrink-0 items-center gap-2 rounded-[11px] bg-card px-3.5 text-[14px] font-medium text-fg shadow-[inset_0_0_0_1px_#3a3a40] transition-colors hover:bg-elev"
          >
            {t('admin.errors.kibana')}
            <span aria-hidden="true">↗</span>
          </a>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {tiles.map((tile) => (
          <Tile key={tile.label} {...tile} />
        ))}
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <Panel
          title={t('admin.errors.chart')}
          hint={
            !tracked
              ? t('admin.errors.notTracked')
              : since
                ? t('admin.errors.since', { date: since })
                : t('admin.errors.total', { total: fmtInt(data.period), days })
          }
        >
          <TimeChart
            days={series.map((row) => row.day)}
            series={[{ key: 'errors', label: t('admin.errors.series'), color: ERROR_COLOR, values: series.map((row) => row.count) }]}
            kind="bar"
            format={fmtInt}
            lang={lang}
            ariaLabel={t('admin.errors.chart')}
            integer
          />
        </Panel>

        <Panel title={t('admin.errors.places')} hint={t('admin.errors.placesHint', { days })}>
          {data.places.length ? (
            <PlaceList rows={data.places} kibana={kibanaUrl} days={days} lang={lang} />
          ) : (
            <p className="text-[14px] text-muted">{t('admin.errors.placesEmpty')}</p>
          )}
        </Panel>
      </div>

      <Panel title={t('admin.errors.recent')} hint={t('admin.errors.recentHint')}>
        {data.recent.length ? (
          <RecentList rows={data.recent} kibana={kibanaUrl} lang={lang} />
        ) : (
          <p className="text-[14px] text-muted">{t('admin.errors.recentEmpty')}</p>
        )}
      </Panel>
    </section>
  );
}
